import * as React from "react";
import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface FieldProps {
  label: string;
  hint?: string;
  example?: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

export function Field({ label, hint, example, error, className, children }: FieldProps) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center gap-1.5">
        <Label>{label}</Label>
        {hint && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground touch-manipulation -ml-1"
                  aria-label="Field hint"
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent>{hint}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {children}
      {example && (
        <p className="text-xs text-muted-foreground">e.g. {example}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

interface NumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  value: number;
  onChange: (n: number) => void;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
}

interface OptionalNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  value: number | null | undefined;
  onChange: (n: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Shown when value is empty (uses built-in default). */
  placeholder?: string;
}

/** Number field that can be cleared; empty maps to `null`. */
export function OptionalNumberInput({
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  className,
  ...rest
}: OptionalNumberInputProps) {
  const displayValue = value != null && Number.isFinite(value) ? value : "";
  return (
    <Input
      type="number"
      inputMode="numeric"
      enterKeyHint="done"
      value={displayValue}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value.trim();
        if (raw === "") {
          onChange(null);
          return;
        }
        const n = Number(raw);
        if (Number.isFinite(n)) onChange(n);
      }}
      min={min}
      max={max}
      step={step}
      className={className}
      {...rest}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  prefix,
  suffix,
  min,
  max,
  step,
  className,
  ...rest
}: NumberInputProps) {
  const displayValue = Number.isFinite(value) ? value : 0;
  return (
    <div className="relative">
      {prefix && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          {prefix}
        </span>
      )}
      <Input
        type="number"
        inputMode="decimal"
        enterKeyHint="done"
        value={displayValue}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        className={cn(prefix ? "pl-7" : "", suffix ? "pr-10" : "", className)}
        {...rest}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}

export function PercentInput({
  value,
  onChange,
  ...rest
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <NumberInput
      value={Math.round(value * 10000) / 100}
      onChange={(n) => onChange(n / 100)}
      suffix="%"
      step={0.1}
      {...rest}
    />
  );
}
